import React, { useState, useRef, useEffect } from 'react';
import { SiYoutube } from 'react-icons/si';
import { getThumbnailUrl } from '../services/youtubeService';
import { getApiBaseUrl, getAuthorizationToken } from '../api-helper';
import YouTubeVideoPlayer from './YouTubeVideoPlayer';

export default function ContextualVideoLink({ tutorialCategoryId }) {
    const [videos, setVideos] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const dropdownRef = useRef(null);

    // Fetch tutorials for this category
    useEffect(() => {
        let cancelled = false;
        const fetchTutorials = async () => {
            try {
                const token = getAuthorizationToken();
                const apiBase = await getApiBaseUrl();
                const response = await fetch(`${apiBase}/api/youtube/tutorials/category/${tutorialCategoryId}`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    signal: AbortSignal.timeout(10000),
                });

                if (!response.ok) return;

                const data = await response.json();
                const rawVideos = data.tutorials || data.videos || data;

                if (!cancelled && Array.isArray(rawVideos) && rawVideos.length > 0) {
                    setVideos(rawVideos.map(v => ({
                        id: v.youtubeVideoId || v.id || v.videoId,
                        title: v.title || '',
                        description: v.description || '',
                    })));
                }
            } catch (e) {
                console.error('[ContextualVideoLink] Fetch failed:', e.message);
            }
        };

        fetchTutorials();
        return () => { cancelled = true; };
    }, [tutorialCategoryId]);

    // Close dropdown on outside click
    useEffect(() => {
        if (!isOpen) return;
        const handleClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [isOpen]);

    // Don't render if no matching videos
    if (!videos || videos.length === 0) return null;

    return (
        <>
            <div className="relative inline-block" ref={dropdownRef}>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="py-1.5 mobile:py-2 px-3 mobile:px-4 border rounded bg-red-600 hover:bg-red-700 transition text-white text-sm mobile:text-base"
                >
                    <span className="hidden max-xl-inclusive:inline">YouTube Tutorials</span>
                    <span className="max-xl-inclusive:hidden"><SiYoutube size={24} /></span>
                </button>

                {/* Dropdown */}
                {isOpen && (
                    <div className="fixed z-[9999] w-80 bg-[#1a1a2e] border border-[#2a2a4a] rounded-lg shadow-xl overflow-hidden"
                         style={{
                             top: dropdownRef.current ? dropdownRef.current.getBoundingClientRect().bottom + 8 : 0,
                             left: dropdownRef.current ? Math.min(
                                 dropdownRef.current.getBoundingClientRect().left,
                                 window.innerWidth - 340
                             ) : 0,
                         }}
                    >
                        <div className="p-2 border-b border-[#2a2a4a]">
                            <p className="text-xs text-[#a0a0b0] font-medium px-1">Related Videos</p>
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                        {videos.map(video => (
                            <button
                                key={video.id}
                                onClick={() => {
                                    setSelectedVideo(video);
                                    setIsOpen(false);
                                }}
                                className="w-full flex items-center gap-3 p-2.5 hover:bg-white/5 transition-colors text-left"
                            >
                                <div className="w-24 h-14 rounded overflow-hidden flex-shrink-0 bg-black/20">
                                    <img
                                        src={getThumbnailUrl(video.id, 'mqdefault')}
                                        alt={video.title}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                    />
                                </div>
                                <span className="text-white text-sm line-clamp-2 leading-snug flex-1">
                                    {video.title}
                                </span>
                            </button>
                        ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Video Player Modal */}
            {selectedVideo && (
                <YouTubeVideoPlayer
                    videoId={selectedVideo.id}
                    title={selectedVideo.title}
                    onClose={() => setSelectedVideo(null)}
                />
            )}
        </>
    );
}
